import {
  Box,
  Button,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { SegmentApiClient } from "../../api/apiClientSegment";
import { CherryPayApi } from "../../api/models";
import { MemberFilterQueryModal } from "../../components/MemberFilterQueryModal/MemberFilterQueryModal";
import { useApiClient } from "../../hooks/useApiClient";
import { useToast } from "../../hooks/useToast";
import { EditIcon, SearchIcon, TrashIcon } from "../../styles/icons";
import { DeleteSegmentModal } from "../../forms/DeleteSegmentModal";

interface SelectSegmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
  onSelect: (segment: CherryPayApi.MemberFilterSegmentSummaryItem) => void;
  notificationScheduleId?: string | null;
  onSegmentDeleted?: (segmentId: string) => void;
}

export const SelectSegmentModal: React.FC<SelectSegmentModalProps> = ({
  isOpen,
  onClose,
  businessId,
  onSelect,
  notificationScheduleId,
  onSegmentDeleted,
}) => {
  const SEGMENT_LIMIT = 100;
  const [searchQuery, setSearchQuery] = useState("");
  const [segments, setSegments] = useState<
    CherryPayApi.MemberFilterSegmentSummaryItem[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(
    null
  );
  const [editingSegment, setEditingSegment] =
    useState<CherryPayApi.MemberFilterSegmentSummaryItem | null>(null);
  const [deletingSegment, setDeletingSegment] =
    useState<CherryPayApi.MemberFilterSegmentSummaryItem | null>(null);
  const [deleteModalState, setDeleteModalState] = useState<{
    isLoading: boolean;
    isBlocked: boolean;
    scheduledCount: number;
    draftCount: number;
  } | null>(null);

  const apiClient = useApiClient();
  const segmentApiClient = useMemo(
    () => new SegmentApiClient(apiClient.cherryPayClient),
    [apiClient]
  );
  const { showErrorToast } = useToast();

  const fetchSegments = useCallback(
    async (q?: string) => {
      setIsLoading(true);
      const result = await segmentApiClient.getMemberFilterSegments(
        businessId,
        { q: q || undefined, limit: SEGMENT_LIMIT }
      );
      if (result.ok) {
        const results = result.data.Results ?? [];
        setSegments(results);
        setHasMore(results.length === SEGMENT_LIMIT);
      }
      setIsLoading(false);
    },
    [businessId, segmentApiClient]
  );

  useEffect(() => {
    if (!isOpen) return;

    setIsLoading(true);
    const timeoutId = setTimeout(() => {
      fetchSegments(searchQuery);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, isOpen, fetchSegments]);

  useEffect(() => {
    if (isOpen) {
      setSearchQuery("");
      setSelectedSegmentId(null);
    }
  }, [isOpen]);

  const handleSelect = () => {
    const segment = segments.find((s) => s.Meta?.Id === selectedSegmentId);
    if (segment) {
      onSelect(segment);
    }
  };

  const handleEditSaved = (
    _query?: string,
    _name?: string,
    _segmentId?: string
  ) => {
    setEditingSegment(null);
    fetchSegments(searchQuery);
  };

  const handleDeleteClick = async (
    segment: CherryPayApi.MemberFilterSegmentSummaryItem
  ) => {
    setDeletingSegment(segment);
    setDeleteModalState({
      isLoading: true,
      isBlocked: false,
      scheduledCount: 0,
      draftCount: 0,
    });

    const result =
      await segmentApiClient.getUpcomingNotificationSchedulesBySegmentId(
        businessId,
        segment.Meta?.Id!,
        notificationScheduleId
      );

    if (!result.ok) {
      showErrorToast("Failed to check notifications using this segment.");
      setDeletingSegment(null);
      setDeleteModalState(null);
      return;
    }

    const schedules = result.data;
    const scheduledCount = schedules.filter(
      (s) => s.NotificationScheduleStatus === "Scheduled"
    ).length;
    const draftCount = schedules.filter(
      (s) => s.NotificationScheduleStatus === "Draft"
    ).length;

    setDeleteModalState({
      isLoading: false,
      isBlocked: schedules.length > 0,
      scheduledCount,
      draftCount,
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deletingSegment) return;
    setDeleteModalState((prev) => (prev ? { ...prev, isLoading: true } : prev));

    const isLastSegment = segments.length === 1;
    const result = await segmentApiClient.deleteMemberFilterSegment(
      businessId,
      deletingSegment.Meta?.Id!
    );

    if (result.ok) {
      const deletedId = deletingSegment.Meta?.Id;
      if (selectedSegmentId === deletedId) {
        setSelectedSegmentId(null);
      }
      setDeletingSegment(null);
      setDeleteModalState(null);
      if (deletedId) {
        onSegmentDeleted?.(deletedId);
      }
      if (isLastSegment) {
        onClose();
      } else {
        fetchSegments(searchQuery);
      }
    } else {
      showErrorToast("Failed to delete segment.");
      setDeleteModalState((prev) =>
        prev ? { ...prev, isLoading: false } : prev
      );
    }
  };

  const handleDeleteModalClose = () => {
    if (deleteModalState?.isLoading) return;
    setDeletingSegment(null);
    setDeleteModalState(null);
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Select Saved Segment</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Search segments"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  borderRadius="full"
                />
              </InputGroup>

              <Box
                border="1px solid"
                borderColor="cherryBorder.100"
                borderRadius="md"
                maxH="360px"
                overflowY="auto"
              >
                {isLoading ? (
                  <HStack justify="center" p={8}>
                    <Spinner size="sm" />
                    <Text fontSize="sm" color="gray.500">
                      Loading segments...
                    </Text>
                  </HStack>
                ) : segments.length === 0 ? (
                  <Text fontSize="sm" color="gray.500" p={4} textAlign="center">
                    No segments found
                  </Text>
                ) : (
                  <RadioGroup
                    value={selectedSegmentId ?? ""}
                    onChange={(value) => setSelectedSegmentId(value || null)}
                  >
                    <VStack align="stretch" spacing={0}>
                      {segments.map((segment, index) => (
                        <HStack
                          key={segment.Meta?.Id}
                          px={4}
                          py={3}
                          borderBottom={
                            index < segments.length - 1 ? "1px solid" : "none"
                          }
                          borderColor="cherryBorder.100"
                          bg={
                            selectedSegmentId === segment.Meta?.Id
                              ? "gray.50"
                              : "white"
                          }
                          cursor="pointer"
                          onClick={() =>
                            setSelectedSegmentId(segment.Meta?.Id ?? null)
                          }
                        >
                          <Radio value={segment.Meta?.Id ?? ""} />
                          <Text fontSize="sm" flex="1">
                            {segment.SegmentName}
                          </Text>
                          <IconButton
                            aria-label="Edit segment"
                            icon={<EditIcon />}
                            variant="ghost"
                            size="sm"
                            color="gray.500"
                            _hover={{ color: "gray.700" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingSegment(segment);
                            }}
                          />
                          <IconButton
                            aria-label="Delete segment"
                            icon={<TrashIcon />}
                            variant="ghost"
                            size="sm"
                            color="gray.500"
                            _hover={{ color: "red.500" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(segment);
                            }}
                          />
                        </HStack>
                      ))}
                    </VStack>
                  </RadioGroup>
                )}
              </Box>

              {hasMore && (
                <Text fontSize="xs" color="gray.500">
                  Not all segments shown. Use the search box to filter the
                  segments.
                </Text>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="cherry-outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="cherry-outline-inverse"
              onClick={handleSelect}
              isDisabled={!selectedSegmentId}
              ml={3}
            >
              Select
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {deletingSegment && deleteModalState && (
        <DeleteSegmentModal
          isOpen={true}
          segmentName={deletingSegment.SegmentName ?? ""}
          scheduledCount={deleteModalState.scheduledCount}
          draftCount={deleteModalState.draftCount}
          isLoading={deleteModalState.isLoading}
          isBlocked={deleteModalState.isBlocked}
          onConfirm={handleDeleteConfirm}
          onClose={handleDeleteModalClose}
        />
      )}

      {editingSegment && (
        <MemberFilterQueryModal
          size="4xl"
          isOpen={!!editingSegment}
          onClose={() => setEditingSegment(null)}
          businessId={businessId}
          memberQueryFilter={editingSegment.MemberFilter ?? ""}
          onSuccess={handleEditSaved}
          segmentMode={true}
          segmentId={editingSegment.Meta?.Id ?? null}
        />
      )}
    </>
  );
};
